import styled from 'styled-components';

export const StaffContainer = styled.div`
  background: var(--bg-section);
  min-height: calc(100vh - 68px);
  padding: 40px 20px 72px;

  > * {
    max-width: 960px;
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: 560px) {
    padding: 28px 16px 56px;
  }
`;

export const StaffHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;

  > div { min-width: 0; flex: 1 1 280px; }

  h1 {
    font-size: var(--text-3xl);
    letter-spacing: -0.5px;
    margin-bottom: 4px;
  }

  p {
    color: var(--text-secondary);
  }
`;

export const InfoBox = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-left: 3px solid var(--primary);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  margin-bottom: 20px;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: 1.55;
  min-width: 0;

  strong { color: var(--text-primary); }

  .login-link {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    margin-top: 10px;

    code {
      background: var(--bg-input);
      padding: 4px 8px;
      border-radius: var(--radius-sm);
      font-size: var(--text-xs);
      overflow-wrap: anywhere;
      min-width: 0;
    }
  }
`;

export const Notice = styled.div`
  background: ${props => (props.$error ? 'var(--danger-subtle)' : 'var(--success-subtle)')};
  border: 1px solid ${props => (props.$error ? 'var(--danger-light)' : 'var(--success-light)')};
  color: ${props => (props.$error ? 'var(--danger-dark)' : 'var(--success-dark)')};
  padding: 12px 16px;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  margin-bottom: 16px;
  overflow-wrap: anywhere;
`;

export const StaffList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const StaffCard = styled.article`
  background: ${props => (props.$revoked) ? 'var(--bg-section)' : 'var(--bg-card)'};
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
  padding: 18px 20px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px 20px;
  min-width: 0;

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
    padding: 16px;
  }
`;

export const StaffInfo = styled.div`
  min-width: 0;

  .name-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
  }

  h3 {
    font-size: var(--text-lg);
    overflow-wrap: anywhere;
    min-width: 0;
  }

  .email {
    color: var(--text-secondary);
    font-size: var(--text-sm);
    overflow-wrap: anywhere;
    margin: 2px 0 8px;
  }

  .meta {
    color: var(--text-muted);
    font-size: var(--text-xs);
    margin-top: 8px;
  }
`;

export const Pill = styled.span`
  display: inline-block;
  max-width: 100%;
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.3px;
  padding: 3px 10px;
  border-radius: var(--radius-round);
  color: ${props => props.$color || 'var(--primary)'};
  background: ${props => props.$bg || 'var(--primary-subtle)'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
`;

export const CardActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;

  button { white-space: nowrap; }

  @media (max-width: 720px) {
    flex-direction: row;
    flex-wrap: wrap;
    button { flex: 1 1 auto; }
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 56px 20px;
  background: var(--bg-card);
  border: 1px dashed var(--border-medium);
  border-radius: var(--radius-lg);

  h3 { margin-bottom: 8px; }

  p {
    color: var(--text-secondary);
    font-size: var(--text-sm);
    margin-bottom: 20px;
  }
`;

/* ---- Modal de alta/edición y de asignación de actividades ---- */

export const ActivityPickerBox = styled.div`
  border: 1.5px solid var(--border-light);
  border-radius: var(--radius-md);
  overflow: hidden;
  min-width: 0;

  .picker-bar {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 6px 12px;
    padding: 8px 12px;
    background: var(--bg-section);
    font-size: var(--text-xs);
    color: var(--text-secondary);
  }

  .picker-bar button {
    background: none;
    border: none;
    color: var(--primary);
    font-family: var(--font-brand);
    font-size: var(--text-xs);
    font-weight: 600;
    cursor: pointer;
    padding: 2px 4px;
  }

  .picker-list {
    max-height: 240px;
    overflow-y: auto;
  }

  .picker-empty {
    padding: 16px;
    font-size: var(--text-sm);
    color: var(--text-secondary);
    text-align: center;
  }
`;

export const PickerItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-top: 1px solid var(--border-light);
  cursor: pointer;
  min-width: 0;

  &:hover { background: var(--primary-subtle); }

  input {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    accent-color: var(--primary);
  }

  .title {
    flex: 1;
    min-width: 0;
    font-size: var(--text-sm);
    overflow-wrap: anywhere;
  }
`;

export const RoleOption = styled.p`
  margin-top: 6px;
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: 1.4;
`;
